import { graphql } from 'react-relay';
import React, { useCallback, useContext, useState } from 'react';
import { useIntl, defineMessages } from 'react-intl';

import ChevronLeftLargeIcon from '@atlaskit/icon/core/chevron-left';
import { token } from '@atlaskit/tokens';
import Button from '@atlaskit/button/default/button';
import LinkButton from '@atlaskit/button/link';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import Inline from '@atlaskit/primitives/inline';
import { xcss } from '@atlaskit/primitives/xcss';

import type {
	Value,
	OptionData,
} from '@confluence/user-and-group-search/entry-points/user-picker.ts';
import { useIsProductAdmin } from '@confluence/current-user/entry-points/useIsProductAdmin.ts';
import { UserAndGroupSearchPicker } from '@confluence/user-and-group-search/entry-points/UserAndGroupSearchPicker.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';
import { ADMIN_SITE_SPACE_PERMISSIONS } from '@confluence/named-routes/entry-points/routes.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { BULK_SPACE_PERMISSIONS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import { useSpaceRoleMode } from '@confluence/space-utils/entry-points/useSpaceRoleMode.ts';
import { useTeamPicker } from '@confluence/permissions-principals/entry-points/useTeamPicker.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { expVal } from '@confluence/feature-experiments';

import type {
	BulkSpacePermissionsPageBulkSetSpacePermissionAsyncMutation as BulkSpacePermissionsPageBulkSetSpacePermissionAsyncMutation$data,
	BulkSpacePermissionsPageBulkSetSpacePermissionAsyncMutationVariables as BulkSpacePermissionsPageBulkSetSpacePermissionAsyncMutation$variables,
} from './__types__/BulkSpacePermissionsPageBulkSetSpacePermissionAsyncMutation.ts';
import {
	BulkSetSpacePermissionSpaceType,
	BulkSetSpacePermissionSubjectType,
	SpacePermissionType,
} from './graphql/__types__/BulkSpacePermissionsMutation.ts';
import { RemovalWarning } from './RemovalWarning.tsx';
import { AgentAccessWarning } from './AgentAccessWarning.tsx';
import {
	SpaceSelectionRadioButtons,
	SpaceSelection,
} from './page-components/SpaceSelectionRadioButtons.tsx';
// eslint-disable-next-line import/no-cycle
import { PermissionSelectionSection } from './page-components/PermissionSelectionSection.tsx';
import { RestrictedView } from './restricted/RestrictedView.tsx';
import { BulkSpacePermissionsModal } from './space-permissions-dialog/BulkSpacePermissionsModal.tsx';
// eslint-disable-next-line import/no-cycle
import { BulkAccessManagementPage } from './BulkAccessManagementPage.tsx';
const BulkSpacePermissionsPageBulkSetSpacePermissionAsyncMutation = graphql`
	mutation BulkSpacePermissionsPageBulkSetSpacePermissionAsyncMutation(
		$spacePermissions: [SpacePermissionType]!
		$spaceTypes: [BulkSetSpacePermissionSpaceType]!
		$subjectId: ID!
		$subjectType: BulkSetSpacePermissionSubjectType!
	) {
		bulkSetSpacePermissionAsync(
			input: {
				spacePermissions: $spacePermissions
				spaceTypes: $spaceTypes
				subjectId: $subjectId
				subjectType: $subjectType
			}
		) {
			success
			errors {
				message
				extensions {
					errorType
					statusCode
				}
			}
			taskId
		}
	}
`;
export const ANALYTICS_SOURCE = 'bulkSpacePermissionsScreen';

export const i18nBulk: {
	backToSpacePermissions: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	bulkAddHeader: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	titleSubtext: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	titleSubtextWithTeams: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	userPickerHeader: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	userPickerSubtext: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	asyncSuccessFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	asyncSuccessFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorFlagTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorFlagDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	userPickerPlaceholder: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	userPickerWithTeamsPlaceholder: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	requirementMessage: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	applyButtonTextAdd: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	applyButtonTextRemove: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	backToSpacePermissions: {
		id: 'bulk-permissions.space-permissions-link',
		defaultMessage: 'Space permissions',
		description:
			'The text is shown as a navigation link in the back button at the top of the bulk space permissions page, directing the admin back to the space permissions settings page.',
	},
	bulkAddHeader: {
		id: 'bulk-permissions.space.header',
		defaultMessage: 'Add people to all spaces',
		description: 'Header text of the bulk permissions page',
	},
	titleSubtext: {
		id: 'bulk-permissions.space.title-subtext',
		defaultMessage:
			'Quickly add (or remove) an individual user or group to all spaces on your Confluence site. This will overwrite any existing access they have, which means it can also be a good way to change access for a user or group in all spaces.',
		description: 'Subtext of a header within a bulk permissions page',
	},
	titleSubtextWithTeams: {
		id: 'bulk-permissions.space.title-subtext-teams',
		defaultMessage:
			'Quickly add (or remove) an individual user, team or group to all spaces on your Confluence site. This will overwrite any existing access they have, which means it can also be a good way to change access for a user or group in all spaces.',
		description: 'Subtext of a header within a bulk permissions page',
	},
	userPickerHeader: {
		id: 'bulk-permissions.space.user-picker-header',
		defaultMessage: 'Choose who to add or remove<asterisk>*</asterisk>',
		description: 'Header text of the user picker, with an asterisk indicating a required field',
	},
	userPickerSubtext: {
		id: 'bulk-permissions.space.user-picker-subtext',
		defaultMessage: 'You may only select one for this action.',
		description:
			'Subtext explaining that only a single user or group can be selected in the user picker field for this action',
	},
	asyncSuccessFlagTitle: {
		id: 'bulk-permissions.space.async.update.success-flag-title',
		defaultMessage: 'Bulk permissions change processing',
		description:
			'The text is shown as the title of a success notification flag that appears after the admin submits a bulk permissions change, indicating that the change is being processed asynchronously.',
	},
	asyncSuccessFlagDescription: {
		id: 'bulk-permissions.space.async.update.success-flag-description',
		defaultMessage: 'These changes can take a while. We’ll notify you when it’s finished.',
		description: 'Async update flag message description',
	},
	errorFlagTitle: {
		id: 'bulk-permissions.space.error-flag-title',
		defaultMessage: 'Bulk permissions change failed',
		description: 'Title for error flag displayed when bulk permissions change fails',
	},
	errorFlagDescription: {
		id: 'bulk-permissions.space.error-flag-description',
		defaultMessage: 'We ran into a problem completing your bulk permissions change. Try again.',
		description: 'Description for error flag displayed when bulk permissions change fails',
	},
	userPickerPlaceholder: {
		id: 'bulk-permissions.space.user-picker-placeholder',
		defaultMessage: 'Enter a person or group',
		description:
			'The text is shown as placeholder text inside the user picker input field on the bulk space permissions page, prompting the admin to search for a person or group.',
	},
	userPickerWithTeamsPlaceholder: {
		id: 'bulk-permissions.space.user-picker-with-teams-placeholder',
		defaultMessage: 'Enter a person, team or group',
		description:
			'The text is shown as placeholder text inside the user picker input field on the bulk space permissions page when the teams feature is enabled, prompting the admin to search for a person, team, or group.',
	},
	requirementMessage: {
		id: 'bulk-permissions.space.requirement-message',
		defaultMessage: '<asterisk>* </asterisk> Required to perform action',
		description:
			'The text is shown at the bottom of the bulk space permissions page to indicate that fields marked with an asterisk are required. The {asterisk} placeholder is replaced with a styled asterisk symbol.',
	},
	applyButtonTextAdd: {
		id: 'bulk-permissions.space.apply-button-text-add',
		defaultMessage: 'Add to all',
		description:
			'Text label for the button that applies the selected user or group role addition to all spaces',
	},
	applyButtonTextRemove: {
		id: 'bulk-permissions.space.apply-button-text-remove',
		defaultMessage: 'Remove from all',
		description:
			'Text label for the button that applies the selected user or group role removal from all spaces',
	},
});

const bulkAddHeaderStyle = xcss({
	font: token('font.heading.large'),
	paddingBottom: 'space.100',
	paddingTop: 'space.300',
});

const bulkSubtextStyle = xcss({
	font: token('font.body'),
});

const userPickerHeaderStyle = xcss({
	font: token('font.body.large'),
	fontWeight: token('font.weight.medium'),
	display: 'flex',
	flexDirection: 'row',
	paddingTop: 'space.300',
	paddingBottom: 'space.100',
});

const asteriskStyle = xcss({
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
	color: 'color.text.accent.red',
});

const userPickerFineTextStyle = xcss({
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage -- Requested suppression for existing token string
	color: 'color.text.accent.gray',
	marginTop: 'space.050',
	font: token('font.body.small'),
});

const contentStyle = xcss({
	marginLeft: 'space.1000',
	marginRight: 'space.1000',
});

const backButtonStyle = xcss({
	marginLeft: 'space.negative.200',
	paddingTop: 'space.300',
});

const userPickerStyle = xcss({
	paddingBottom: 'space.050',
});

const requirementMessageStyle = xcss({
	font: token('font.body'),
	paddingTop: 'space.300',
});

const getPrincipalId = (principal: Value | undefined) => {
	if (!principal || Array.isArray(principal)) return '';
	const principalData = principal as OptionData;
	return principalData.id || '';
};

export const BulkSpacePermissionsPage: React.ComponentType<Omit<unknown, 'flags'>> = withFlags(
	withErrorBoundary({
		attribution: Attribution.PERMISSIONS_EXPERIENCE,
	})(({ flags }: { flags: FlagsStateContainer }) => {
		const intl = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const { isProductAdmin, isAdminCheckLoading } = useIsProductAdmin();
		const { edition } = useSessionData();
		const { isRbacEnabled } = useSpaceRoleMode();
		const { pickerProps: teamPickerProps } = useTeamPicker();
		const isFreeEdition = edition === ConfluenceEdition.FREE;
		const [spacePermissions, setSpacePermissions] = useState<SpacePermissionType[]>([
			SpacePermissionType.VIEW_SPACE,
		]);
		const [stagedPrincipal, setStagedPrincipal] = React.useState<Value | undefined>(undefined);
		const [spaceSelection, setSpaceSelection] = React.useState<SpaceSelection>(
			SpaceSelection.AllExcludingPersonal,
		);

		const experienceTracker = useContext(ExperienceTrackerContext);
		const [showDialog, setShowDialog] = React.useState(false);

		const isRemoval = !spacePermissions.includes(SpacePermissionType.VIEW_SPACE);

		const closeDialog = useCallback(() => {
			setShowDialog(false);
		}, [setShowDialog]);

		const setInitState = useCallback(() => {
			setStagedPrincipal(undefined);
			setSpaceSelection(SpaceSelection.AllExcludingPersonal);
			setSpacePermissions([SpacePermissionType.VIEW_SPACE]);
		}, []);

		const onMutationErrored = useCallback(
			(error: Error) => {
				closeDialog();
				void flags.showErrorFlag({
					id: 'bulk-space-permissions.add.error',
					title: intl.formatMessage(i18nBulk.errorFlagTitle),
					description: intl.formatMessage(i18nBulk.errorFlagDescription),
					isAutoDismiss: true,
				});
				experienceTracker.stopOnError({
					name: BULK_SPACE_PERMISSIONS_EXPERIENCE,
					error,
				});
			},
			[closeDialog, flags, intl, experienceTracker],
		);

		const onAsyncMutationSubmitted = useCallback(
			(data) => {
				closeDialog();
				/**
				 * The backend is counting the mutation as a success for some exceptions
				 * but returning an error in the mutation response.  We need to check for
				 * these here.
				 */
				if (data?.bulkSetSpacePermissionAsync?.errors?.length > 0) {
					onMutationErrored(
						new Error(
							'Bulk space permissions mutation failed',
							data?.bulkSetSpacePermissionAsync?.errors,
						),
					);
				} else {
					void flags.showSuccessFlag({
						id: 'bulk-space-permissions.async.update.success',
						title: intl.formatMessage(i18nBulk.asyncSuccessFlagTitle),
						description: intl.formatMessage(i18nBulk.asyncSuccessFlagDescription),
						isAutoDismiss: true,
					});
					experienceTracker.succeed({
						name: BULK_SPACE_PERMISSIONS_EXPERIENCE,
					});
				}
				setInitState();
			},
			[closeDialog, setInitState, onMutationErrored, flags, intl, experienceTracker],
		);

		const getBulkSpacePermissionSubjectType = useCallback((): BulkSetSpacePermissionSubjectType => {
			if (!stagedPrincipal || Array.isArray(stagedPrincipal)) {
				return BulkSetSpacePermissionSubjectType.USER;
			}
			const principalData = stagedPrincipal as OptionData;
			return principalData.type === 'user'
				? BulkSetSpacePermissionSubjectType.USER
				: BulkSetSpacePermissionSubjectType.GROUP;
		}, [stagedPrincipal]);

		const getBulkSpacePermissionSpaceType =
			useCallback((): (BulkSetSpacePermissionSpaceType | null)[] => {
				switch (spaceSelection) {
					case SpaceSelection.AllExcludingPersonal: {
						return [
							BulkSetSpacePermissionSpaceType.GLOBAL,
							BulkSetSpacePermissionSpaceType.COLLABORATION,
							BulkSetSpacePermissionSpaceType.KNOWLEDGE_BASE,
						];
					}
					case SpaceSelection.AllIncludingPersonal: {
						return [
							BulkSetSpacePermissionSpaceType.PERSONAL,
							BulkSetSpacePermissionSpaceType.GLOBAL,
							BulkSetSpacePermissionSpaceType.COLLABORATION,
							BulkSetSpacePermissionSpaceType.KNOWLEDGE_BASE,
						];
					}
					case SpaceSelection.OnlyPersonal: {
						return [BulkSetSpacePermissionSpaceType.PERSONAL];
					}
					default: {
						return [null];
					}
				}
			}, [spaceSelection]);
		const [bulkUpdateAsync, { loading: asyncLoading, error: asyncError }] = useMutationInterop<
			BulkSpacePermissionsPageBulkSetSpacePermissionAsyncMutation$data,
			BulkSpacePermissionsPageBulkSetSpacePermissionAsyncMutation$variables
		>(BulkSpacePermissionsPageBulkSetSpacePermissionAsyncMutation, {
			onCompleted: onAsyncMutationSubmitted,
			onError: (err: Error) => {
				closeDialog();
				onMutationErrored(err);
			},
			variables: {
				spacePermissions,
				subjectType: getBulkSpacePermissionSubjectType(),
				subjectId: getPrincipalId(stagedPrincipal),
				spaceTypes: getBulkSpacePermissionSpaceType(),
			},
		});
		const openDialog = useCallback(() => {
			experienceTracker.start({
				name: BULK_SPACE_PERMISSIONS_EXPERIENCE,
			});
			setShowDialog(true);
		}, [setShowDialog, experienceTracker]);

		const handleBackClick = useCallback(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					actionSubject: 'button',
					action: 'clicked',
					actionSubjectId: 'defaultspaceperms',
					source: ANALYTICS_SOURCE,
				},
			}).fire();
		}, [createAnalyticsEvent]);

		const onSpaceRadioChange = useCallback(
			(e: React.ChangeEvent<HTMLInputElement>) => {
				setSpaceSelection(e.target.value as SpaceSelection);
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						actionSubject: 'radio',
						action: 'selected',
						actionSubjectId: 'bulkSpacePermissionsType',
						source: ANALYTICS_SOURCE,
						attributes: {
							spaceSelection: e.target.value,
						},
					},
				}).fire();
			},
			[createAnalyticsEvent],
		);

		const onPrincipalChange = useCallback(
			(received: Value) => {
				// Only send an analytic event if a principal isn't deselected
				if (received && !Array.isArray(received)) {
					const principalData = received as OptionData;
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							actionSubject: 'principal',
							action: 'selected',
							actionSubjectId: 'bulkSpacePermissionsPrincipal',
							source: ANALYTICS_SOURCE,
							attributes: {
								principal: principalData.type,
								verified: principalData.type === 'team' ? principalData.verified : 'N/A',
							},
						},
					}).fire();
				}
				setStagedPrincipal(received);
			},
			[createAnalyticsEvent],
		);

		const onDialogConfirm = useCallback(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					actionSubject: 'button',
					action: 'clicked',
					actionSubjectId: 'bulkSpacePermissionsConfirm',
					source: ANALYTICS_SOURCE,
					attributes: {
						verified:
							(stagedPrincipal as OptionData)?.type === 'team'
								? (stagedPrincipal as OptionData)?.verified
								: 'N/A',
						principalType: (stagedPrincipal as OptionData)?.type || 'N/A',
						principal: (stagedPrincipal as OptionData)?.type,
						action: isRemoval ? 'remove' : 'add',
					},
				},
			}).fire();
			void bulkUpdateAsync();
		}, [createAnalyticsEvent, bulkUpdateAsync, isRemoval, stagedPrincipal]);

		const onDialogCancel = useCallback(() => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					actionSubject: 'button',
					action: 'clicked',
					actionSubjectId: 'bulkSpacePermissionsCancel',
					source: ANALYTICS_SOURCE,
				},
			}).fire();
			closeDialog();
			experienceTracker.abort({
				name: BULK_SPACE_PERMISSIONS_EXPERIENCE,
				reason: 'User closed the dialog',
			});
		}, [createAnalyticsEvent, experienceTracker, closeDialog]);

		// Don't render the page until we know if the user is a product admin
		// to avoid a flicker of the restricted view.
		if (isAdminCheckLoading) {
			return null;
		}

		if (!isProductAdmin) {
			return <RestrictedView />;
		}

		const pickerResultsFilter = (options: OptionData[]) => {
			// TEMPORARY WORKAROUND:
			// Smart User Picker does not currently expose a reliable field to identify guest users.
			// We're using the presence of the "GUEST" lozenge as a proxy to filter out guest users.
			// This should be revisited once the platform team adds a proper field
			// to accurately indicate guest status.
			return options.filter((option) => {
				const { lozenge } = option;

				const isGuest =
					typeof lozenge === 'object' &&
					lozenge !== null &&
					'text' in lozenge &&
					lozenge.text === 'GUEST';

				return !isGuest;
			});
		};

		const placeholderText = intl.formatMessage(
			teamPickerProps.includeTeamsUpdates
				? i18nBulk.userPickerWithTeamsPlaceholder
				: i18nBulk.userPickerPlaceholder,
		);

		return isRbacEnabled ? (
			<BulkAccessManagementPage />
		) : (
			<>
				{asyncError && <ErrorDisplay error={asyncError} />}
				<Box
					xcss={
						expVal('cc_perms_enable_rbac_admin_modernization', 'isEnabled', false)
							? undefined
							: contentStyle
					}
				>
					{!expVal('cc_perms_enable_rbac_admin_modernization', 'isEnabled', false) && (
						<Box xcss={backButtonStyle}>
							<LinkButton
								appearance="subtle"
								onClick={handleBackClick}
								href={ADMIN_SITE_SPACE_PERMISSIONS.toUrl({
									tab: 'allspaces',
								})}
								iconBefore={(iconProps) => <ChevronLeftLargeIcon {...iconProps} size="small" />}
							>
								{intl.formatMessage(i18nBulk.backToSpacePermissions)}
							</LinkButton>
						</Box>
					)}

					<Box xcss={bulkAddHeaderStyle}>{intl.formatMessage(i18nBulk.bulkAddHeader)}</Box>
					<Box xcss={bulkSubtextStyle}>
						{intl.formatMessage(
							teamPickerProps.includeTeamsUpdates
								? i18nBulk.titleSubtextWithTeams
								: i18nBulk.titleSubtext,
						)}
					</Box>

					<Box xcss={userPickerHeaderStyle}>
						{intl.formatMessage(i18nBulk.userPickerHeader, {
							asterisk: (chunks: React.ReactNode[]) => <Box xcss={asteriskStyle}>{chunks}</Box>,
						})}
					</Box>
					<Box xcss={userPickerStyle}>
						<UserAndGroupSearchPicker
							ariaLabel={placeholderText}
							data-test-id="user-and-group-search"
							fieldId="bulk-permissions-user-picker"
							includeGroup
							withUsers
							isMulti={false}
							smart
							placeholder={placeholderText}
							// @ts-ignore - Type 'null' is not assignable to type 'string | ((input: { inputValue: string; }) => string | null)'
							// Using `ts-ignore` and not `ts-expect-error` here as TypeScript will fail on this line without `ts-ignore` but with `ts-expect-error`
							// This error was introduced after upgrading to TypeScript 5
							noOptionsMessage={null}
							onChange={onPrincipalChange}
							value={stagedPrincipal || []}
							smartFilterOptions={pickerResultsFilter}
							isDisabled={isFreeEdition}
							includeTeams={teamPickerProps.includeTeams}
							includeTeamsUpdates={teamPickerProps.includeTeamsUpdates}
							verifiedTeams={teamPickerProps.verifiedTeams}
						/>
					</Box>
					<AgentAccessWarning
						stagedPrincipals={
							stagedPrincipal && !Array.isArray(stagedPrincipal)
								? [stagedPrincipal as OptionData]
								: []
						}
					/>
					<Box xcss={userPickerFineTextStyle}>{intl.formatMessage(i18nBulk.userPickerSubtext)}</Box>

					<SpaceSelectionRadioButtons
						spaceSelection={spaceSelection}
						onRadioChange={onSpaceRadioChange}
						isDisabled={isFreeEdition}
					/>

					<PermissionSelectionSection
						spacePermissions={spacePermissions}
						setSpacePermissions={setSpacePermissions}
						isDisabled={isFreeEdition}
					/>

					{isRemoval && <RemovalWarning />}

					<Button
						appearance="primary"
						onClick={openDialog}
						isDisabled={isFreeEdition || stagedPrincipal === undefined}
					>
						{intl.formatMessage(
							isRemoval ? i18nBulk.applyButtonTextRemove : i18nBulk.applyButtonTextAdd,
						)}
					</Button>

					<Inline xcss={requirementMessageStyle}>
						{intl.formatMessage(i18nBulk.requirementMessage, {
							asterisk: (chunks: React.ReactNode[]) => <Box xcss={asteriskStyle}>{chunks}</Box>,
						})}
					</Inline>
				</Box>
				{showDialog && (
					<BulkSpacePermissionsModal
						principalName={
							!stagedPrincipal || Array.isArray(stagedPrincipal)
								? ''
								: (stagedPrincipal as OptionData).name || ''
						}
						isRemoval={isRemoval}
						spaceSelection={spaceSelection}
						isLoading={asyncLoading}
						onConfirm={onDialogConfirm}
						onCancel={onDialogCancel}
					/>
				)}
			</>
		);
	}),
);
